import { Grid } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcInfiniteLoading, MprcRequestIndicator } from "components";
import { ButtonSave } from "components/buttons";
import { MprcContainerViewPort } from "components/MprcContainerViewPort";
import { MprcRequestErrorDialog } from "components/MprcRequestErrorDialog";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { CreateUnitTypePayload, ManagedUnitTypeResponse } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import ResourceUtils from "lib/ResourceUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditUnitTypeFooter } from "views/foundation/settings/Home/UnitTypes/EditUnitType/EditUnitTypeFooter";
import { EditUnitTypeHeader } from "views/foundation/settings/Home/UnitTypes/EditUnitType/EditUnitTypeHeader";
import { EditUnitTypeMainBox } from "views/foundation/settings/Home/UnitTypes/EditUnitType/EditUnitTypeMainBox";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    unitTypeId?: string | undefined;
    onClose: (reload: boolean) => void;
}


interface IBoxValidations {
    mainBox: boolean;
}


////////////////////////////////////////////////////////////////////////////
/// FUNCTIONS
////////////////////////////////////////////////////////////////////////////

///
function getEmptyDto(locales: string[]): ManagedUnitTypeResponse {
    return {
        unitTypeId: null,
        nameR: ResourceUtils.buildEmptyLocaleInputCollection(locales),
        branchNameR: ResourceUtils.buildEmptyLocaleInputCollection(locales),
        externalCode: "",
        isRequired: false,
        allowMultiSelection: true,
        showOnUserProfile: true,
        defineJobsAndPositions: false,
        thumbnail: null,
        recordInfo: null,
        settings: {
            canChangeIsRequired: true
        }
    };
}


///
function getInitialValidations(unitTypeId: string | undefined): IBoxValidations {
    const edit = unitTypeId != undefined && unitTypeId != "";
    return { mainBox: edit };
}


///
function buildPostDto(dto: ManagedUnitTypeResponse): CreateUnitTypePayload {
    return {
        nameR: dto.nameR,
        externalCode: dto.externalCode,
        branchNameR: dto.branchNameR,
        isRequired: dto.isRequired,
        allowMultiSelection: dto.allowMultiSelection,
        showOnUserProfile: dto.showOnUserProfile,
        defineJobsAndPositions: dto.defineJobsAndPositions,
        thumbnailId: dto.thumbnail ? dto.thumbnail.fileId : null
    };
}

///
function getHeaderItems(
    canSave: boolean,
    handleSave: () => void,
) {
    const formHeaderOptions: React.ReactNode[] = [];

    formHeaderOptions.push(<ButtonSave key="btnSave" rounded size="large" variant="text" onClick={handleSave} disabled={!canSave} />);

    return formHeaderOptions;
}


////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Tipos de Unidades Administrativa
 * @param props
 */
export const EditUnitType: React.FC<IProps> = (props) => {
    ////////////////////////////////////////////////////////////////////////////

    const { onClose } = props;
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const api = useCoreApi();
    const session = useSession();

    const unitTypeId = useRef<string | undefined>(props.unitTypeId);
    const boxValidations = useRef<IBoxValidations>(getInitialValidations(props.unitTypeId));
    const reloadOnClose = useRef<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewData, setViewData] = useState<ManagedUnitTypeResponse>(() => getEmptyDto(session.config.supportedLocales));
    const [loadDto, setLoadDto] = useState<ManagedUnitTypeResponse>(() => LibUtils.deepCopy(viewData));
    const [canSave, setCanSave] = useState<boolean>(false);
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();


    ////////////////////////////////////////////////////////////////////////////
    /// Handles
    ////////////////////////////////////////////////////////////////////////////


    ///
    const handleUpdateViewData = (newDto: ManagedUnitTypeResponse) => {
        setViewData(newDto);
    };

    ///
    const handleSave = () => {
        if (canSave) setSaving(true);
    };

    ///
    const handleClose = () => {
        onClose(false);
    };


    ///
    const refreshData = (dto: ManagedUnitTypeResponse) => {
        dto.nameR = dto.nameR ?? ResourceUtils.buildEmptyLocaleInputCollection(session.config.supportedLocales);
        dto.branchNameR = dto.branchNameR ?? ResourceUtils.buildEmptyLocaleInputCollection(session.config.supportedLocales);
        setLoadDto(LibUtils.deepCopy(dto));
        setViewData(dto);
        setViewFieldsErrors(undefined);
    };

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        setViewFieldsErrors(r.fieldsWithError);
        setViewError(r.otherErrors);
        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };


    ////////////////////////////////////////////////////////////////////////////
    /// Effect's
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar um DTO:
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.unitTypeManagement.managedGetUnitType({ unitTypeId: unitTypeId.current! });
                refreshData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            setLoading(false);
        }

        if (loading) getData();

    }, [loading]);


    // Ao salvar um DTO:
    useEffect(() => {
        async function postData() {
            try {
                var result: string | any = "";

                if (unitTypeId.current) {
                    await api.v1.unitTypeManagement.updateUnitType({ unitTypeId: unitTypeId.current!, ...buildPostDto(viewData) });
                }
                else {
                    result = await api.v1.unitTypeManagement.createUnitType(buildPostDto(viewData));
                    unitTypeId.current = result.data;
                }
                reloadOnClose.current = true;
                enqueueSnackbar(t("Settings.UnitTypes.Edit.SaveSuccess"), { variant: "success" });
                onClose(true);
            }
            catch (e) {
                resolveErrors(e);
            }
            setSaving(false);
        }

        if (saving) postData();

    }, [saving]);


    // Ao editar o DTO atual:
    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);
        const checkCanSave = Object.values(boxValidations.current).filter(x => x === false).length == 0 && isRecordChanged;

        setCanSave(checkCanSave);
        setRecordChanged(isRecordChanged);
    }, [viewData]);


    // Ao ocorrer erro de validação no servidor:
    useEffect(() => {
        if (viewFieldsErrors) {
            setTimeout(() => {
                document.querySelector<HTMLInputElement>("input[aria-invalid='true']")?.focus();
            }, 500);
        }
    }, [viewFieldsErrors]);



    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator
                visible={saving}
                type={"post"}
            />

            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => setViewError(undefined)}
            />

            <Grid container direction="column" height="100%">
                <Grid width="100%">
                    <EditUnitTypeHeader
                        reloadOnClose={reloadOnClose.current}
                        recordChanged={recordChanged}
                        onClose={handleClose}
                        items={getHeaderItems(canSave, handleSave)}
                    />
                </Grid>

                <Grid size="grow" width="100%" overflow="hidden">

                    <Grid container direction="column" height="100%">
                        <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar}>

                            {loading
                                ? <MprcInfiniteLoading visible />
                                : <MprcContainerViewPort>

                                    <EditUnitTypeMainBox
                                        viewData={viewData}
                                        viewErrors={viewFieldsErrors}
                                        onFormUpdate={handleUpdateViewData}
                                        onFormValidation={(valid) => boxValidations.current.mainBox = valid}
                                        onRefreshForm={(disableSave) => disableSave ? setCanSave(false) : undefined}
                                    />

                                    {
                                        unitTypeId.current && viewData.recordInfo &&
                                        <EditUnitTypeFooter
                                            recordInfo={viewData.recordInfo}
                                            unitTypeId={unitTypeId.current}
                                        />
                                    }
                                </MprcContainerViewPort>
                            }

                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
